/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiSelectors, uiTypes } from '@opengeoweb/store';
import { MenuOptions, SoundingsMenuBar } from './MenuBar';
import { soundingsActions } from '../../store/reducer';
import {
  getSelectedSourcesAmount,
  getShouldHideLocationMarkers,
  getIsLegendVisible,
} from '../../store/selectors';
import type { SoundingsRootState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';

interface MenuBarConnectProps {
  viewId: string;
  source?: uiTypes.Source;
  selectedLocationName?: string;
}

export const MenuBarConnect = ({
  viewId,
  source,
  selectedLocationName,
}: MenuBarConnectProps): React.ReactElement => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(soundingsActions.registerSoundingsView({ viewId }));
  }, [dispatch, viewId]);

  const currentActiveMapIdSources = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsSources),
  );
  const currentActiveMapIdLocations = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsLocations),
  );

  const currentActiveMapIdsoundingsManager = useSelector(
    (store: CoreAppStore) =>
      uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsManager),
  );

  const currentActiveMapIdProfileData = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsProfileData),
  );

  const isSourcesOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.SoundingsSources),
  );
  const isLocationsOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.SoundingsLocations),
  );

  const isSoundingsManagerOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.SoundingsManager),
  );

  const isProfileDataOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(
      store,
      uiTypes.DialogTypes.SoundingsProfileData,
    ),
  );

  const sourcesAmount = useSelector((state: SoundingsRootState) =>
    getSelectedSourcesAmount(state, viewId),
  );
  const shouldHideLocationMarkers = useSelector((state: SoundingsRootState) =>
    getShouldHideLocationMarkers(state, viewId),
  );

  const isLegendVisible = useSelector((state: SoundingsRootState) =>
    getIsLegendVisible(state, viewId),
  );

  const toggleLegend = React.useCallback((): void => {
    dispatch(
      soundingsActions.toggleLegendVisibility({
        isLegendVisible: !isLegendVisible,
        viewId,
        origin: SoundingsActionOrigin.soundingsComponent,
      }),
    );
  }, [dispatch, isLegendVisible, viewId]);

  const toggleLocationMarkers = React.useCallback(
    (shouldHide: boolean): void => {
      dispatch(
        soundingsActions.toggleLocationMarkers({
          shouldHideLocationMarkers: shouldHide,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  const dispatchActiveMapId = React.useCallback(
    (
      dialogType: uiTypes.DialogTypes,
      isOpen: boolean,
      activeMapId: string,
    ): void =>
      void dispatch(
        uiActions.setActiveMapIdForDialog({
          type: dialogType,
          mapId: viewId,
          setOpen: activeMapId !== viewId ? true : !isOpen,
          source,
        }),
      ),
    [dispatch, viewId, source],
  );
  const handleClick = React.useCallback(
    (menuType: MenuOptions): void => {
      switch (menuType) {
        case MenuOptions.soundingsManager:
          dispatchActiveMapId(
            uiTypes.DialogTypes.SoundingsManager,
            isSoundingsManagerOpenInStore,
            currentActiveMapIdsoundingsManager,
          );
          break;
        case MenuOptions.sources:
          dispatchActiveMapId(
            uiTypes.DialogTypes.SoundingsSources,
            isSourcesOpenInStore,
            currentActiveMapIdSources,
          );
          break;
        case MenuOptions.locations: {
          const newIsLocationsOpen =
            currentActiveMapIdLocations !== viewId
              ? true
              : !isLocationsOpenInStore;
          dispatchActiveMapId(
            uiTypes.DialogTypes.SoundingsLocations,
            isLocationsOpenInStore,
            currentActiveMapIdLocations,
          );
          dispatch(
            soundingsActions.setLocationsMenuOpen({
              viewId,
              isLocationsDialogOpen: newIsLocationsOpen,
              origin: SoundingsActionOrigin.soundingsComponent,
            }),
          );
          break;
        }
        case MenuOptions.profileData:
          dispatchActiveMapId(
            uiTypes.DialogTypes.SoundingsProfileData,
            isProfileDataOpenInStore,
            currentActiveMapIdProfileData,
          );
          break;
        default:
      }
    },
    [
      dispatch,
      dispatchActiveMapId,
      viewId,
      isSoundingsManagerOpenInStore,
      currentActiveMapIdsoundingsManager,
      isSourcesOpenInStore,
      currentActiveMapIdSources,
      isLocationsOpenInStore,
      currentActiveMapIdLocations,
      isProfileDataOpenInStore,
      currentActiveMapIdProfileData,
    ],
  );

  const isOpen = {
    [MenuOptions.sources]:
      currentActiveMapIdSources === viewId && isSourcesOpenInStore,
    [MenuOptions.locations]:
      currentActiveMapIdLocations === viewId && isLocationsOpenInStore,
    [MenuOptions.soundingsManager]:
      currentActiveMapIdsoundingsManager === viewId &&
      isSoundingsManagerOpenInStore,
    [MenuOptions.profileData]:
      currentActiveMapIdProfileData === viewId && isProfileDataOpenInStore,
  };

  return (
    <SoundingsMenuBar
      handleClick={handleClick}
      isLegendVisible={isLegendVisible}
      isOpen={isOpen}
      selectedLocationName={selectedLocationName}
      selectedSourcesAmount={sourcesAmount}
      shouldHideMarkers={shouldHideLocationMarkers}
      toggleLegend={toggleLegend}
      toggleMarkers={toggleLocationMarkers}
    />
  );
};
